/**
 * O PAÍS DO CLÃ (11/09/2026) — o nome de cada um, e o palpite pelo navegador.
 *
 * **Não é a bandeira do clã** (D25, `ClanBandeira.vue`). Aquela é a cara do clã, e pode ser um
 * upload; esta diz de onde ele é, e só vale um código da lista. Ver §12 de
 * `fishinggame-backend/docs/ATUALIZACAO-1.1-CLAS.md`.
 *
 * ## O nome vem do navegador, e não do i18n
 *
 * `Intl.DisplayNames` nomeia os 255 códigos de duas letras nas quatro línguas do jogo, com a grafia
 * que cada língua usa — escrever isso à mão seriam mil linhas de JSON para errar em algumas. Só os
 * nove com hífen (Inglaterra, Catalunha, Ascensão…) não têm código ISO de país, e esses moram em
 * `clan.pais.lugares`, nas quatro línguas. `tests/unit/paises/paises.spec.js` confere que nenhum
 * dos 264 sai sem nome.
 */
import { CODIGOS_DE_PAIS } from './paises.gerado';

/** Código → posição na folha. Montado uma vez: a folha não muda enquanto a página vive. */
export const POSICAO_NA_FOLHA = new Map(CODIGOS_DE_PAIS.map((codigo, i) => [codigo, i]));

export function paisExiste(codigo) {
  return typeof codigo === 'string' && POSICAO_NA_FOLHA.has(codigo);
}

/** A chave do i18n de uma subdivisão. `-` vira `_`: o hífen no meio de um caminho do vue-i18n é pedir problema. */
export function chaveDoLugar(codigo) {
  return `clan.pais.lugares.${codigo.replace('-', '_')}`;
}

const nomeadores = new Map();

function nomeador(idioma) {
  if (!nomeadores.has(idioma)) {
    let instancia = null;
    try {
      instancia = new Intl.DisplayNames([idioma], { type: 'region', fallback: 'none' });
    } catch (e) {
      // Navegador sem `Intl.DisplayNames` (anterior a 2021): o código em maiúsculas é o que sobra.
    }
    nomeadores.set(idioma, instancia);
  }
  return nomeadores.get(idioma);
}

/**
 * O nome do país na língua do jogo.
 *
 * `traduzir` é o `$t` de quem chama: só as subdivisões passam por ele. Nunca devolve vazio — um
 * país sem nome no escolhedor seria um botão que ninguém sabe o que faz.
 */
export function nomeDoPais(codigo, idioma, traduzir) {
  if (!paisExiste(codigo)) return '';
  if (codigo.includes('-')) return traduzir(chaveDoLugar(codigo));
  return nomeador(idioma)?.of(codigo.toUpperCase()) || codigo.toUpperCase();
}

/**
 * O país que o navegador sugere: a região da primeira língua preferida que tenha uma — `pt-BR` dá
 * `br`. É só o ponto de partida do escolhedor, e por isso nunca adivinha a partir da língua sem
 * região: `pt` sozinho tanto pode ser Brasil quanto Portugal, e errar ali é pior que não sugerir.
 */
export function paisDoNavegador(linguas = typeof navigator !== 'undefined' ? navigator.languages : []) {
  for (const lingua of linguas || []) {
    const regiao = /^[a-z]{2,3}-([a-z]{2})\b/i.exec(lingua)?.[1]?.toLowerCase();
    if (regiao && paisExiste(regiao)) return regiao;
  }
  return null;
}

/** Tira acento e caixa: exigir o til de "Japão" num campo de busca é exigir que a pessoa já saiba escrever. */
export function semAcento(texto) {
  return String(texto || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}
